import React, { useState } from 'react';
import { HiUpload, HiEye, HiTrash, HiPlus } from 'react-icons/hi';
import { supabase } from '../supabaseClient';

const BirthdayCardCreator = ({ standalone = false, onCardCreated, onClose }) => {
  const [cardData, setCardData] = useState({
    title: '',
    message: '',
    sender_name: '',
    recipient_name: '',
    background_color: '#1a1a2e',
    text_color: '#ffffff',
    font_family: 'Space Grotesk',
    is_permanent: false,
    card_images: [], // Changed to array
    card_videos: [], // Changed to array
    card_audios: [], // Changed to array
    password: '',
    is_private: false
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [creationSuccess, setCreationSuccess] = useState(false);
  const [newCardUrl, setNewCardUrl] = useState('');
  const [uploading, setUploading] = useState({
    image: false,
    video: false,
    audio: false
  });

  const uploadFile = async (file, type) => {
    try {
      setUploading(prev => ({ ...prev, [type]: true }));

      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('birthday-cards')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('birthday-cards')
        .getPublicUrl(filePath);

      // Update card data with the URL
      setCardData(prev => ({
        ...prev,
        [`card_${type}s`]: [...prev[`card_${type}s`], publicUrl]
      }));

      setUploading(prev => ({ ...prev, [type]: false }));

      return publicUrl;
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      setUploading(prev => ({ ...prev, [type]: false }));
      alert(`Error uploading ${type}. Please try again.`);
    }
  };

  const removeFile = async (type, index) => {
    try {
      // Get the URL of the file to remove
      const url = cardData[`card_${type}s`][index];
      if (!url) return;

      // Extract the file name from the URL
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop();

      // Delete file from Supabase storage
      const { error } = await supabase.storage
        .from('birthday-cards')
        .remove([fileName]);

      if (error) {
        console.error(`Error removing ${type}:`, error);
        alert(`Error removing ${type}. Please try again.`);
      } else {
        // Remove the URL from card data
        setCardData(prev => ({
          ...prev,
          [`card_${type}s`]: prev[`card_${type}s`].filter((_, i) => i !== index)
        }));
        console.log(`${type} removed successfully`);
      }
    } catch (error) {
      console.error(`Error removing ${type}:`, error);
      alert(`Error removing ${type}. Please try again.`);
    }
  };

  const handleFileChange = async (e, type) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Validate file type
    const validTypes = {
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      video: ['video/mp4', 'video/webm', 'video/ogg'],
      audio: ['audio/mpeg', 'audio/wav', 'audio/ogg']
    };

    for (const file of files) {
      if (!validTypes[type].includes(file.type)) {
        alert(`Please select a valid ${type} file.`);
        continue;
      }

      // Validate file size
      const maxSize = type === 'video' ? 30 * 1024 * 1024 : 5 * 1024 * 1024;
      const maxSizeLabel = type === 'video' ? '30MB' : '5MB';

      if (file.size > maxSize) {
        alert(`${type.charAt(0).toUpperCase() + type.slice(1)} files must be less than ${maxSizeLabel}.`);
        continue;
      }

      await uploadFile(file, type);
    }
  };

  const handleCreateCard = async () => {
    setIsCreating(true);
    try {
      // Calculate expiration date (24 hours from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 1);

      // Generate a unique creator ID and store it in localStorage
      const creatorId = localStorage.getItem('birthdayCardCreatorId') || Math.random().toString(36).substring(2, 15);
      localStorage.setItem('birthdayCardCreatorId', creatorId);

      // For backward compatibility, we'll store the first file URL in the old fields
      const cardToInsert = {
        ...cardData,
        card_image_url: cardData.card_images.length > 0 ? cardData.card_images[0] : null,
        card_video_url: cardData.card_videos.length > 0 ? cardData.card_videos[0] : null,
        card_audio_url: cardData.card_audios.length > 0 ? cardData.card_audios[0] : null,
        expires_at: cardData.is_permanent ? null : expiresAt.toISOString(),
        creator_id: cardData.is_private ? creatorId : null
      };

      // Log the card data being inserted
      console.log('Inserting card data:', cardToInsert);

      const { data, error } = await supabase
        .from('birthday_cards')
        .insert([cardToInsert])
        .select();

      if (error) {
        console.error('Error creating birthday card:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        alert(`Error creating birthday card: ${error.message}`);
      } else {
        // Successfully created
        setCreationSuccess(true);
        setNewCardUrl(`${window.location.origin}/birthday-card/${data[0].id}`);
        // Notify parent component
        if (onCardCreated) {
          onCardCreated(data[0]);
        }
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error creating birthday card. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  if (isPreviewing) {
    const previewStyle = {
      backgroundColor: cardData.background_color || '#1a1a2e',
      color: cardData.text_color || '#ffffff',
      fontFamily: cardData.font_family || 'Space Grotesk, sans-serif'
    };

    return (
      <div className="glass-morphism-strong rounded-2xl p-6 mb-12">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-space-grotesk font-bold text-white">
            Card Preview
          </h3>
          <button
            onClick={() => setIsPreviewing(false)}
            className="text-gray-400 hover:text-white"
          >
            Close Preview
          </button>
        </div>

        <div
          className="rounded-2xl p-8 mb-6 transition-all duration-300"
          style={previewStyle}
        >
          <h1 className="text-3xl md:text-display-2 font-bold mb-4">
            {cardData.title || 'Happy Birthday!'}
          </h1>
          <p className="text-xl md:text-2xl mb-8">
            For <span className="font-bold">{cardData.recipient_name || 'Someone Special'}</span>
          </p>

          {/* Media Content Preview */}
          {cardData.card_images.length > 0 && (
            <div className="mb-8">
              {cardData.card_images.map((imageUrl, index) => (
                <div key={index} className="relative mb-4">
                  <img
                    src={imageUrl}
                    alt={`Birthday card ${index + 1}`}
                    className="max-w-full h-auto rounded-2xl mx-auto shadow-lg"
                  />
                  <button
                    onClick={() => removeFile('image', index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-100 hover:bg-red-600 transition-colors"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {cardData.card_videos.length > 0 && (
            <div className="mb-8">
              {cardData.card_videos.map((videoUrl, index) => (
                <div key={index} className="relative mb-4">
                  <div className="relative pt-[56.25%] rounded-2xl overflow-hidden shadow-lg mx-auto">
                    <video
                      src={videoUrl}
                      controls
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                  <button
                    onClick={() => removeFile('video', index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-100 hover:bg-red-600 transition-colors"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {cardData.card_audios.length > 0 && (
            <div className="mb-8">
              {cardData.card_audios.map((audioUrl, index) => (
                <div key={index} className="relative bg-black/30 rounded-2xl p-4 backdrop-blur-sm border border-white/10 mx-auto mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">Audio Message {index + 1}</span>
                    <button
                      onClick={() => removeFile('audio', index)}
                      className="bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                  <audio
                    src={audioUrl}
                    controls
                    className="w-full rounded-lg"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Message Preview */}
          <div className="bg-black/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg md:text-xl leading-relaxed whitespace-pre-wrap">
                {cardData.message || 'No message provided.'}
              </p>
            </div>
          </div>

          {/* Sender Preview */}
          <div>
            <p className="text-lg">
              With love from <span className="font-bold">{cardData.sender_name || 'A Friend'}</span>
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row space-x-0 sm:space-x-4 space-y-4 sm:space-y-0">
          <button
            onClick={() => setIsPreviewing(false)}
            className="btn-secondary"
          >
            Back to Editor
          </button>
          <button
            onClick={handleCreateCard}
            disabled={isCreating}
            className="bg-gradient-to-r from-accent-green to-accent-blue text-white font-medium py-2 px-6 rounded-lg hover:from-accent-green/90 hover:to-accent-blue/90 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Creating...' : 'Create Card'}
          </button>
        </div>
      </div>
    );
  }

  if (creationSuccess && !standalone) {
    return (
      <div className="glass-morphism-strong rounded-2xl p-8 max-w-2xl mx-auto mb-12">
        <div className="text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-2xl font-space-grotesk font-bold text-white mb-4">
            Birthday Card Created Successfully!
          </h3>
          <p className="text-gray-300 mb-6">
            Your custom birthday card has been created. Share the link below with your friend!
          </p>

          <div className="bg-white/5 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-400 mb-2">Share this link:</p>
            <div className="flex items-center">
              <input
                type="text"
                readOnly
                value={newCardUrl}
                className="flex-1 bg-black/20 text-white rounded-l-lg px-4 py-2 text-sm font-mono min-w-0"
              />
              <button
                onClick={() => navigator.clipboard.writeText(newCardUrl)}
                className="bg-accent-green text-black px-4 py-2 rounded-r-lg hover:bg-accent-green/80 transition-colors whitespace-nowrap"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="flex space-x-4 justify-center">
            <button
              onClick={() => {
                setCreationSuccess(false);
                setCardData({
                  title: '',
                  message: '',
                  sender_name: '',
                  recipient_name: '',
                  background_color: '#1a1a2e',
                  text_color: '#ffffff',
                  font_family: 'Space Grotesk',
                  is_permanent: false,
                  card_images: [],
                  card_videos: [],
                  card_audios: [],
                  password: ''
                });
              }}
              className="btn-primary"
            >
              Create Another Card
            </button>
            <a
              href={newCardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              View Card
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (creationSuccess && standalone) {
    return (
      <div className="glass-morphism-strong rounded-2xl p-8 max-w-2xl mx-auto mb-12">
        <div className="text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-2xl font-space-grotesk font-bold text-white mb-4">
            Birthday Card Created Successfully!
          </h3>
          <p className="text-gray-300 mb-6">
            Your custom birthday card has been created. Share the link below with your friend!
          </p>

          <div className="bg-white/5 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-400 mb-2">Share this link:</p>
            <div className="flex items-center">
              <input
                type="text"
                readOnly
                value={newCardUrl}
                className="flex-1 bg-black/20 text-white rounded-l-lg px-4 py-2 text-sm font-mono min-w-0"
              />
              <button
                onClick={() => navigator.clipboard.writeText(newCardUrl)}
                className="bg-accent-green text-black px-4 py-2 rounded-r-lg hover:bg-accent-green/80 transition-colors whitespace-nowrap"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
            <button
              onClick={() => {
                setCreationSuccess(false);
                setCardData({
                  title: '',
                  message: '',
                  sender_name: '',
                  recipient_name: '',
                  background_color: '#1a1a2e',
                  text_color: '#ffffff',
                  font_family: 'Space Grotesk',
                  is_permanent: false,
                  card_images: [],
                  card_videos: [],
                  card_audios: [],
                  password: ''
                });
              }}
              className="btn-primary"
            >
              Create Another Card
            </button>
            <a
              href={newCardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              View Card
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`glass-morphism-strong rounded-2xl p-6 mb-12 ${standalone ? '' : ''}`}>
      {!standalone && (
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-space-grotesk font-bold text-white">
            Create Birthday Card
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              Close
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Card Title</label>
            <input
              type="text"
              value={cardData.title}
              onChange={(e) => setCardData({ ...cardData, title: e.target.value })}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
              placeholder="Happy Birthday!"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Recipient Name</label>
            <input
              type="text"
              value={cardData.recipient_name}
              onChange={(e) => setCardData({ ...cardData, recipient_name: e.target.value })}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
              placeholder="Friend's name"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Your Name</label>
            <input
              type="text"
              value={cardData.sender_name}
              onChange={(e) => setCardData({ ...cardData, sender_name: e.target.value })}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
              placeholder="Your name"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Message</label>
            <textarea
              value={cardData.message}
              onChange={(e) => setCardData({ ...cardData, message: e.target.value })}
              rows={4}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
              placeholder="Write your special birthday message here..."
            />
          </div>

          {/* File Uploads */}
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Images</label>
            <div className="flex items-center space-x-2">
              <label className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white cursor-pointer hover:bg-black/30 transition-colors">
                <div className="flex items-center justify-center">
                  <HiUpload className="mr-2" />
                  {cardData.card_images.length > 0 ? 'Add More Images' : 'Upload Images'}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'image')}
                  className="hidden"
                  disabled={uploading.image}
                  multiple
                />
              </label>
              {uploading.image && (
                <div className="text-sm text-gray-400">Uploading...</div>
              )}
            </div>
            {cardData.card_images.length > 0 && (
              <div className="mt-2 flex items-center justify-between">
                <div className="text-sm text-green-400">{cardData.card_images.length} image(s) uploaded successfully!</div>
              </div>
            )}
            {/* Image Previews */}
            {cardData.card_images.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {cardData.card_images.map((imageUrl, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={imageUrl}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      onClick={() => removeFile('image', index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-100 transition-opacity"
                    >
                      <HiTrash className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Videos</label>
            <div className="flex items-center space-x-2">
              <label className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white cursor-pointer hover:bg-black/30 transition-colors">
                <div className="flex items-center justify-center">
                  <HiUpload className="mr-2" />
                  {cardData.card_videos.length > 0 ? 'Add More Videos' : 'Upload Videos'}
                </div>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleFileChange(e, 'video')}
                  className="hidden"
                  disabled={uploading.video}
                  multiple
                />
              </label>
              {uploading.video && (
                <div className="text-sm text-gray-400">Uploading...</div>
              )}
            </div>
            {cardData.card_videos.length > 0 && (
              <div className="mt-2 flex items-center justify-between">
                <div className="text-sm text-green-400">{cardData.card_videos.length} video(s) uploaded successfully!</div>
              </div>
            )}
            {/* Video Previews */}
            {cardData.card_videos.length > 0 && (
              <div className="mt-3 space-y-3">
                {cardData.card_videos.map((videoUrl, index) => (
                  <div key={index} className="relative group">
                    <div className="relative pt-[56.25%] rounded-lg overflow-hidden border border-white/10">
                      <video
                        src={videoUrl}
                        controls
                        className="absolute inset-0 w-full h-full"
                      />
                    </div>
                    <button
                      onClick={() => removeFile('video', index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-100 transition-opacity"
                    >
                      <HiTrash className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-300 mb-2">Audio</label>
            <div className="flex items-center space-x-2">
              <label className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white cursor-pointer hover:bg-black/30 transition-colors">
                <div className="flex items-center justify-center">
                  <HiUpload className="mr-2" />
                  {cardData.card_audios.length > 0 ? 'Add More Audio' : 'Upload Audio'}
                </div>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => handleFileChange(e, 'audio')}
                  className="hidden"
                  disabled={uploading.audio}
                  multiple
                />
              </label>
              {uploading.audio && (
                <div className="text-sm text-gray-400">Uploading...</div>
              )}
            </div>
            {cardData.card_audios.length > 0 && (
              <div className="mt-2 flex items-center justify-between">
                <div className="text-sm text-green-400">{cardData.card_audios.length} audio file(s) uploaded successfully!</div>
              </div>
            )}
            {/* Audio Previews */}
            {cardData.card_audios.length > 0 && (
              <div className="mt-3 space-y-3">
                {cardData.card_audios.map((audioUrl, index) => (
                  <div key={index} className="relative group bg-black/30 rounded-lg p-3 backdrop-blur-sm border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Audio {index + 1}</span>
                      <button
                        onClick={() => removeFile('audio', index)}
                        className="text-red-500 hover:text-red-400"
                      >
                        <HiTrash className="w-4 h-4" />
                      </button>
                    </div>
                    <audio
                      src={audioUrl}
                      controls
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Background Color</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={cardData.background_color}
                onChange={(e) => setCardData({ ...cardData, background_color: e.target.value })}
                className="w-10 h-10 border-0 bg-transparent cursor-pointer"
              />
              <input
                type="text"
                value={cardData.background_color}
                onChange={(e) => setCardData({ ...cardData, background_color: e.target.value })}
                className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Text Color</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={cardData.text_color}
                onChange={(e) => setCardData({ ...cardData, text_color: e.target.value })}
                className="w-10 h-10 border-0 bg-transparent cursor-pointer"
              />
              <input
                type="text"
                value={cardData.text_color}
                onChange={(e) => setCardData({ ...cardData, text_color: e.target.value })}
                className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Font Family</label>
            <select
              value={cardData.font_family}
              onChange={(e) => setCardData({ ...cardData, font_family: e.target.value })}
              className="w-full bg-black border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
            >
              <option value="Space Grotesk">Space Grotesk</option>
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Georgia">Georgia</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Courier New">Courier New</option>
            </select>
          </div>

          <div className="mb-6">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={cardData.is_permanent}
                onChange={(e) => setCardData({ ...cardData, is_permanent: e.target.checked })}
                className="w-4 h-4 text-accent-green bg-black/20 border-white/10 rounded focus:ring-accent-green"
              />
              <span className="text-gray-300">Make this card permanent (doesn't expire)</span>
            </label>
          </div>

          {/* Password Protection */}
          <div className="mb-6">
            <label className="block text-gray-300 mb-2">Password Protection (Optional)</label>
            <input
              type="password"
              value={cardData.password}
              onChange={(e) => setCardData({ ...cardData, password: e.target.value })}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-green"
              placeholder="Leave blank for no password"
            />
            <p className="text-sm text-gray-400 mt-1">
              Add a password to protect your card. Leave blank for public access.
            </p>
          </div>

          {/* Private Card Option */}
          <div className="mb-6">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={cardData.is_private}
                onChange={(e) => setCardData({ ...cardData, is_private: e.target.checked })}
                className="w-4 h-4 text-accent-green bg-black/20 border-white/10 rounded focus:ring-accent-green"
              />
              <span className="text-gray-300">Make this card private (only you can see it on the birthday page)</span>
            </label>
            {cardData.is_private && (
              <p className="text-sm text-gray-400 mt-1">
                Private cards will only be visible to you on the birthday page. You'll need to use the direct link to share them.
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row space-x-0 sm:space-x-4 space-y-4 sm:space-y-0">
            <button
              onClick={() => setIsPreviewing(true)}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-medium py-3 px-4 rounded-lg hover:from-purple-700 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
            >
              <HiEye className="inline mr-2" /> Preview
            </button>

            <button
              onClick={handleCreateCard}
              disabled={isCreating}
              className="flex-1 bg-gradient-to-r from-accent-green to-accent-blue text-white font-medium py-3 px-4 rounded-lg hover:from-accent-green/90 hover:to-accent-blue/90 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : 'Create Card'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirthdayCardCreator;